import classnames from 'classnames/bind';
import { FloatingArrow, type FloatingArrowProps } from '@floating-ui/react';
import type { ComponentProps } from 'react';
import { Icon, IconSize, type IconVariant } from 'app/components/core/Icon';
import { TooltipVariant } from './constants';
import * as styles from './Tooltip.css';

const cx = classnames.bind(styles);

const ICON_VARIANT: Record<TooltipVariant, IconVariant | null> = {
    [TooltipVariant.DEFAULT]: null,
    [TooltipVariant.WARNING]: 'status-warning-solid',
    [TooltipVariant.ERROR]: 'status-alert-solid'
} as const;

export interface TooltipProps extends ComponentProps<'div'> {
    variant?: TooltipVariant;
    className?: string;
    arrowRef: FloatingArrowProps['ref'];
    context: FloatingArrowProps['context'];
}

export function Tooltip({
    children,
    variant = TooltipVariant.DEFAULT,
    className,
    arrowRef,
    context,
    ...props
}: TooltipProps) {
    const baseClassName = cx('tooltip', variant);
    const contentClassName = cx('content', className);
    const pl = context.placement;
    const isEdgeAligned = pl.endsWith('start') || pl.endsWith('end');
    const icon = ICON_VARIANT[variant];

    if (!children) {
        return null;
    }

    return (
        <div popover="manual" role="tooltip" className={baseClassName} {...props}>
            <FloatingArrow
                className={styles.arrow}
                ref={arrowRef}
                context={context}
                d="M0,14 L6.125,7.825 Q7,7,7.825,7.825 L14,14"
                strokeWidth={1}
                staticOffset={isEdgeAligned ? '10%' : null}
            />
            {icon && <Icon className={styles.icon} variant={icon} size={IconSize.EXTRA_SMALL} />}
            <div className={contentClassName}>{children}</div>
        </div>
    );
}
